import { Component } from '@theme/component';
import { ThemeEvents, VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * @typedef {Object} SelectionTarget
 * @property {HTMLOptionElement | HTMLInputElement} datasetSource
 * @property {HTMLElement | null} focusTarget
 */

/**
 * @typedef {Object} VariantPickerChangeDetail
 * @property {string[]} [optionValueIds]
 * @property {string} [productUrl]
 * @property {string} [targetId]
 */

class VariantPickerComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#handleChange);
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#handleChange);
    super.disconnectedCallback();
  }

  /** @param {Event} event */
  #handleChange = (event) => {
    const selection = this.#resolveSelectionTarget(event.target);
    if (!selection) return;

    this.#updateSelectionMetadata(selection.datasetSource);

    const selectedVariantId = selection.datasetSource?.dataset.variantId || '';
    const variantSelectedEvent = new VariantSelectedEvent({ id: selectedVariantId });
    Object.assign(variantSelectedEvent.detail, {
      optionValueIds: this.selectedOptionValueIds,
      productUrl: selection.datasetSource?.dataset.productUrl || this.dataset.url,
      targetId: selection.focusTarget?.id || selection.datasetSource?.id,
    });

    this.dispatchEvent(variantSelectedEvent);
  };

  /**
   * @param {EventTarget | null} target
   * @returns {SelectionTarget | null}
   */
  #resolveSelectionTarget(target) {
    if (!(target instanceof HTMLElement)) return null;

    if (target instanceof HTMLSelectElement) {
      const selectedOption = target.selectedOptions[0];
      if (!selectedOption) return null;
      return {
        datasetSource: selectedOption,
        focusTarget: target,
      };
    }

    if (target instanceof HTMLOptionElement && target.parentElement instanceof HTMLSelectElement) {
      return {
        datasetSource: target,
        focusTarget: target.parentElement,
      };
    }

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      return {
        datasetSource: target,
        focusTarget: target,
      };
    }

    return null;
  }

  /**
   * @param {HTMLOptionElement | HTMLInputElement} target
   */
  #updateSelectionMetadata(target) {
    if (target instanceof HTMLOptionElement && target.parentElement instanceof HTMLSelectElement) {
      const select = target.parentElement;
      Array.from(select.options).forEach((option) => option.removeAttribute('selected'));
      target.setAttribute('selected', 'selected');
      return;
    }

    if (target instanceof HTMLInputElement && target.type === 'radio') {
      const radios = this.querySelectorAll(`input[name="${CSS.escape(target.name)}"]`);
      radios.forEach((radio) => {
        if (radio === target) {
          radio.setAttribute('checked', 'checked');
        } else {
          radio.removeAttribute('checked');
        }
      });
    }
  }

  get selectedOptionValueIds() {
    const selector = 'input[type="radio"][data-option-value-id]:checked, select option[data-option-value-id][selected]';
    /** @type {string[]} */
    const ids = [];

    for (const element of this.querySelectorAll(selector)) {
      if (!(element instanceof HTMLElement)) continue;
      const optionId = element.dataset.optionValueId;
      if (typeof optionId === 'string') {
        ids.push(optionId);
      }
    }

    return ids;
  }
}

class ProductInfoComponent extends Component {
  /** @type {AbortController | undefined} */
  #abortController;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
  }

  disconnectedCallback() {
    this.removeEventListener(ThemeEvents.variantSelected, this.#handleVariantSelected);
    this.#abortController?.abort();
    super.disconnectedCallback();
  }

  /**
   * @param {CustomEvent<VariantPickerChangeDetail & { resource: any }>} event
   */
  #handleVariantSelected = (event) => {
    if (!(event.target instanceof Element) || !this.contains(event.target)) return;

    const { optionValueIds = [], productUrl, targetId } = event.detail ?? {};
    const optionIds = Array.isArray(optionValueIds) ? optionValueIds : [];

    this.#renderProductInfo({
      optionValueIds: optionIds,
      productUrl: productUrl || this.dataset.url,
      targetId,
    });
  };

  /**
   * @param {{ optionValueIds: string[]; productUrl?: string; targetId?: string }} params
   */
  #renderProductInfo({ optionValueIds, productUrl, targetId }) {
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const resolvedProductUrl = productUrl || this.dataset.url || window.location.pathname;
    const requestUrl = this.#buildRequestUrl(resolvedProductUrl, optionValueIds);

    fetch(requestUrl, { signal: this.#abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const documentHTML = new DOMParser().parseFromString(responseText, 'text/html');
        const nextProductInfo = documentHTML.getElementById(this.id);

        if (!nextProductInfo) {
          throw new Error(`Product info component with id ${this.id} not found in response.`);
        }

        const previousProductId = this.dataset.productId;
        morph(this, nextProductInfo);

        const variant = this.#extractVariant(documentHTML);
        const newProductId = nextProductInfo.dataset.productId;
        const productChanged = previousProductId !== newProductId;

        const nextUrl = nextProductInfo.dataset.url || resolvedProductUrl;
        this.#updatePageState(variant, nextUrl);
        this.#dispatchVariantUpdate(variant, documentHTML, productChanged ? nextProductInfo.dataset : undefined);
        this.#restoreFocus(targetId);
      })
      .catch((error) => {
        if (error.name === 'AbortError') return;
        console.error(error);
      })
      .finally(() => {
        this.#abortController = undefined;
      });
  }

  /**
   * @param {string} productUrl
   * @param {string[]} [optionValueIds=[]]
   */
  #buildRequestUrl(productUrl, optionValueIds = []) {
    const url = new URL(productUrl, window.location.origin);
    const sectionId = this.dataset.sectionId || this.dataset.section;
    if (!sectionId) {
      throw new Error('Product info component is missing a section id.');
    }
    url.searchParams.set('section_id', sectionId);

    if (optionValueIds.length) {
      url.searchParams.set('option_values', optionValueIds.join(','));
    } else {
      url.searchParams.delete('option_values');
    }

    return url.toString();
  }

  /**
   * @param {Document} documentHTML
   * @returns {any}
   */
  #extractVariant(documentHTML) {
    const variantScript = documentHTML.querySelector('[data-selected-variant]');
    if (!variantScript) return null;

    try {
      return JSON.parse(variantScript.textContent || '');
    } catch (error) {
      console.warn('Unable to parse selected variant payload', error);
      return null;
    }
  }

  /**
   * @param {any} variant
   * @param {Document} html
   * @param {DOMStringMap | undefined} newProductData
   */
  #dispatchVariantUpdate(variant, html, newProductData) {
    const productId = this.dataset.productId ?? '';
    /** @type {{ html: Document; productId: string; newProduct?: { id: string; url: string; } }} */
    const data = {
      html,
      productId,
    };

    if (newProductData && newProductData.productId && newProductData.url) {
      data.newProduct = {
        id: newProductData.productId,
        url: newProductData.url,
      };
    }

    this.dispatchEvent(new VariantUpdateEvent(variant, this.id, data));
  }

  /**
   * @param {any} variant
   * @param {string} productUrl
   */
  #updatePageState(variant, productUrl) {
    if (!productUrl) return;
    this.#updateBrowserUrl(productUrl, variant?.id);
  }

  /**
   * @param {string} productUrl
   * @param {string | undefined} variantId
   */
  #updateBrowserUrl(productUrl, variantId) {
    if (this.dataset.updateUrl !== 'true') return;

    const url = new URL(productUrl, window.location.origin);
    if (variantId) {
      url.searchParams.set('variant', variantId);
    } else {
      url.searchParams.delete('variant');
    }

    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  /**
   * @param {string | undefined} targetId
   */
  #restoreFocus(targetId) {
    if (!targetId) return;
    requestAnimationFrame(() => {
      document.getElementById(targetId)?.focus();
    });
  }
}

if (!customElements.get('variant-picker-component')) {
  customElements.define('variant-picker-component', VariantPickerComponent);
}

if (!customElements.get('product-info-component')) {
  customElements.define('product-info-component', ProductInfoComponent);
}

